import React from 'react';
import { t, type TranslationParams } from '../../services/locale';
import { labeledCheckboxStyles as styles } from './LabeledCheckbox.styles';

/**
 * Generic "I agree to <terms>" row. Pass `agreeLabelKey` / `linkLabelKey`;
 * `react-directive` re-renders after the React locale dictionary changes.
 *
 * `checkboxId` must land on the <input> (SCA). Known ids:
 * class-booking-accept-terms, purchase-membership-accept-terms.
 */
export type LabeledCheckboxData = {
  accepted?: boolean;
  agreeLabelKey?: string;
  linkLabelKey?: string;
  translationParams?: TranslationParams;
  checkboxId?: string;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
  onClickLink?: () => void;
  dataAttributes?: Record<string, string>;
};

type Props = {
  data: LabeledCheckboxData;
};

function unescapeAmpersand(value: string): string {
  return value.replaceAll('&amp;', '&');
}

function resolveLabel(
  key: string | undefined,
  translationParams: TranslationParams | undefined
): string {
  if (!key) {
    return '';
  }
  const resolved = t(key, translationParams);
  return unescapeAmpersand(typeof resolved === 'string' ? resolved : key);
}

export function LabeledCheckbox({ data }: Props) {
  if (!data) {
    return null;
  }

  const {
    accepted = false,
    agreeLabelKey,
    linkLabelKey,
    translationParams,
    checkboxId,
    disabled = false,
    onChange,
    onClickLink,
    dataAttributes,
  } = data;

  const extraInputProps: Record<string, string> = {};
  if (dataAttributes) {
    Object.keys(dataAttributes).forEach((key) => {
      if (key.indexOf('data-') === 0) {
        extraInputProps[key] = dataAttributes[key];
      }
    });
  }

  const resolvedAgreeLabel = resolveLabel(agreeLabelKey, translationParams);
  const resolvedLinkLabel = resolveLabel(linkLabelKey, translationParams);
  const hasLink = !!resolvedLinkLabel && !!onClickLink;

  const trimmedAgreeLabel = resolvedAgreeLabel.trim();
  if (!trimmedAgreeLabel && !hasLink) {
    return null;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    if (onChange) {
      onChange(event.target.checked);
    }
  };

  const handleClickLink = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (onClickLink) {
      onClickLink();
    }
  };

  const boxStyle = disabled ? { ...styles.box, ...styles.boxDisabled } : styles.box;
  const inputStyle = disabled ? { ...styles.input, ...styles.inputDisabled } : styles.input;

  return (
    <div className="labeled-checkbox" style={styles.wrapper}>
      <span style={boxStyle}>
        <input
          id={checkboxId || undefined}
          type="checkbox"
          style={inputStyle}
          checked={accepted}
          disabled={disabled}
          onChange={handleChange}
          {...extraInputProps}
        />
        {accepted ? (
          <svg style={styles.check} viewBox="0 0 16 16" aria-hidden="true">
            <polyline
              points="3.5,8.5 6.5,11.5 12.5,4.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        ) : null}
      </span>
      <span style={styles.text}>
        <label htmlFor={checkboxId || undefined} style={styles.label}>
          {hasLink ? `${resolvedAgreeLabel} ` : resolvedAgreeLabel}
        </label>
        {hasLink ? (
          <a href="" style={styles.link} onClick={handleClickLink}>
            {resolvedLinkLabel}
          </a>
        ) : null}
      </span>
    </div>
  );
}
